// src/services/api.js
import axios from 'axios';

// Use import.meta.env instead of process.env for Vite
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3015/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        const response = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
        const { accessToken } = response.data;
        
        localStorage.setItem('accessToken', accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - redirect to login
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tenantId');
        
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    
    // Handle other errors
    if (error.response?.status === 403) {
      console.error('Permission denied:', error.response?.data?.error);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authService = {
  login: (credentials) => api.post('/auth/login', credentials).then(res => res.data),
  register: (data) => api.post('/auth/register', data).then(res => res.data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data).then(res => res.data),
  resetPassword: (data) => api.post('/auth/reset-password', data).then(res => res.data),
  logout: (data) => api.post('/auth/logout', data).then(res => res.data),
  getProfile: () => api.get('/auth/profile').then(res => res.data),
  updateProfile: (data) => api.put('/auth/profile', data).then(res => res.data),
  changePassword: (data) => api.put('/auth/change-password', data).then(res => res.data),
  refreshToken: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    return api.post('/auth/refresh-token', { refreshToken }).then(res => res.data);
  },
};

export const taxHelper = {
    // Calculate inclusive price
    getInclusivePrice: (exclusivePrice, taxRate = 16) => {
        return exclusivePrice * (1 + taxRate / 100);
    },
    
    // Calculate tax amount
    getTaxAmount: (exclusivePrice, taxRate = 16) => {
        return exclusivePrice * (taxRate / 100);
    },
    
    // Format currency
    formatCurrency: (amount) => {
        return `KES ${amount.toLocaleString()}`;
    }
};

// Product API
export const productService = {
  getProducts: (params) => api.get('/products', { params }).then(res => res.data),
  getProduct: (id) => api.get(`/products/${id}`).then(res => res.data),
  createProduct: (data) => api.post('/products', data).then(res => res.data),
  updateProduct: (id, data) => api.put(`/products/${id}`, data).then(res => res.data),
  deleteProduct: (id) => api.delete(`/products/${id}`).then(res => res.data),
  updateStock: (id, data) => api.patch(`/products/${id}/stock`, data).then(res => res.data),
  bulkImport: (data) => api.post('/products/bulk-import', data).then(res => res.data),
  getCategories: () => api.get('/products/categories').then(res => res.data),
  getLowStock: () => api.get('/products/low-stock').then(res => res.data),
};

// Transaction API
export const transactionService = {
  getTransactions: (params) => api.get('/transactions', { params }).then(res => res.data),
  getInvoices: (params) => api.get('/invoices', { params }).then(res => res.data),  // ✅ Added
  getTransaction: (id) => api.get(`/transactions/${id}`).then(res => res.data),
  createTransaction: (data) => api.post('/transactions', data).then(res => res.data),
  createPOSSale: (data) => api.post('/transactions/pos-sale', data).then(res => res.data),
  createInvoice: (data) => api.post('/transactions/invoice', data).then(res => res.data),
  updateTransaction: (id, data) => api.put(`/transactions/${id}`, data).then(res => res.data),
  recordPayment: (id, data) => api.post(`/transactions/${id}/pay`, data).then(res => res.data),
  voidTransaction: (id, reason) => api.post(`/transactions/${id}/void`, { reason }).then(res => res.data),
  sendReminder: (id) => api.post(`/transactions/${id}/reminder`).then(res => res.data),
  downloadInvoice: (id) => api.get(`/transactions/${id}/download`, { responseType: 'blob' }).then(res => res.data),
  getAgingReport: () => api.get('/transactions/aging').then(res => res.data),
  getZReport: (date) => api.get('/transactions/z-report', { params: { date } }).then(res => res.data),
};

// Customer API
export const customerService = {
  getCustomers: (params) => api.get('/customers', { params }).then(res => res.data),
  getCustomer: (id) => api.get(`/customers/${id}`).then(res => res.data),
  createCustomer: (data) => api.post('/customers', data).then(res => res.data),
  updateCustomer: (id, data) => api.put(`/customers/${id}`, data).then(res => res.data),
  deleteCustomer: (id) => api.delete(`/customers/${id}`).then(res => res.data),
  sendSMS: (id, message) => api.post(`/customers/${id}/sms`, { message }).then(res => res.data),
  getTransactions: (id, params) => api.get(`/customers/${id}/transactions`, { params }).then(res => res.data),
  getSummary: () => api.get('/customers/summary').then(res => res.data),
};

// Expense API
export const expenseService = {
  getExpenses: (params) => api.get('/expenses', { params }).then(res => res.data),
  getExpense: (id) => api.get(`/expenses/${id}`).then(res => res.data),
  createExpense: (data) => {
    // If FormData (for file upload)
    if (data instanceof FormData) {
      return api.post('/expenses', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(res => res.data);
    }
    return api.post('/expenses', data).then(res => res.data);
  },
  updateExpense: (id, data) => api.put(`/expenses/${id}`, data).then(res => res.data),
  deleteExpense: (id) => api.delete(`/expenses/${id}`).then(res => res.data),
  getSummary: (params) => api.get('/expenses/summary', { params }).then(res => res.data),
  getVendors: () => api.get('/expenses/vendors').then(res => res.data),
  createVendor: (data) => api.post('/expenses/vendors', data).then(res => res.data),
  getCategories: () => api.get('/expenses/categories').then(res => res.data),
};

// In api.js, add to reportService
export const reportService = {
  getSalesReport: (params) => api.get('/reports/sales', { params }).then(res => res.data),
  getTaxReport: (params) => api.get('/reports/tax', { params }).then(res => res.data),
  getProfitLoss: (params) => api.get('/reports/profit-loss', { params }).then(res => res.data),
  getInventoryReport: () => api.get('/reports/inventory').then(res => res.data),
  getAgingReport: (params) => api.get('/reports/aging', { params }).then(res => res.data),
  getServiceReport: (params) => api.get('/reports/services', { params }).then(res => res.data),
  getReportSummary: (params) => api.get('/reports/summary', { params }).then(res => res.data),
  exportReport: (type, params, format) => 
    api.get(`/reports/export/${type}`, { params, responseType: 'blob' }).then(res => res.data),
};

// For customer payments (tenant's own M-Pesa)
export const mpesaService = {
    initiateSTKPush: (data) => api.post('/mpesa/stk-push', data).then(res => res.data),
    queryStatus: (checkoutRequestId) => api.post('/mpesa/query', { checkoutRequestId }).then(res => res.data),
};

// For SMS top-ups (Autobell's M-Pesa)
export const mpesaTopupService = {
    initiateTopUp: (data) => api.post('/mpesa-topup/initiate', data).then(res => res.data),
    queryStatus: (checkoutRequestId) => api.post('/mpesa-topup/status', { checkoutRequestId }).then(res => res.data),
};
// SMS API
export const smsService = {
  sendBulkSMS: (data) => api.post('/sms/bulk', data).then(res => res.data),
  sendDebtReminders: (data) => api.post('/sms/debt-reminders', data).then(res => res.data),
  getHistory: (params) => api.get('/sms/history', { params }).then(res => res.data),
  getBalance: () => api.get('/sms/balance').then(res => res.data),
  topUpBalance: (amount, paymentMethod) => api.post('/sms/topup', { amount, paymentMethod }).then(res => res.data),
  getTemplates: () => api.get('/sms/templates').then(res => res.data),
getCustomTemplates: () => api.get('/sms/custom-templates').then(res => res.data),
createCustomTemplate: (data) => api.post('/sms/custom-templates', data).then(res => res.data),
updateCustomTemplate: (id, data) => api.put(`/sms/custom-templates/${id}`, data).then(res => res.data),
deleteCustomTemplate: (id) => api.delete(`/sms/custom-templates/${id}`).then(res => res.data),
};

// Dashboard API
export const dashboardService = {
  getDashboardData: (params) => api.get('/dashboard', { params }).then(res => res.data),
  getKPIs: () => api.get('/dashboard/kpis').then(res => res.data),
  getSalesChart: (params) => api.get('/dashboard/sales-chart', { params }).then(res => res.data),
  getRecentTransactions: () => api.get('/dashboard/recent-transactions').then(res => res.data),
  getLowStockAlerts: () => api.get('/dashboard/low-stock').then(res => res.data),
  getActivityFeed: () => api.get('/dashboard/activity').then(res => res.data),
};

// Settings API
export const settingsService = {
  getSettings: () => api.get('/settings').then(res => res.data),
  updateSettings: (data) => api.put('/settings', data).then(res => res.data),
  getUsers: (params) => api.get('/settings/users', { params }).then(res => res.data),
  createUser: (data) => api.post('/settings/users', data).then(res => res.data),
  updateUser: (id, data) => api.put(`/settings/users/${id}`, data).then(res => res.data),
  deleteUser: (id) => api.delete(`/settings/users/${id}`).then(res => res.data),
  getTaxSettings: () => api.get('/settings/tax').then(res => res.data),
  updateTaxSettings: (data) => api.put('/settings/tax', data).then(res => res.data),
};

// Super Admin API
export const superAdminService = {
  getTenants: (params) => api.get('/admin/tenants', { params }).then(res => res.data),
  createTenant: (data) => api.post('/admin/tenants', data).then(res => res.data),
  updateTenant: (id, data) => api.put(`/admin/tenants/${id}`, data).then(res => res.data),
  deleteTenant: (id) => api.delete(`/admin/tenants/${id}`).then(res => res.data),
  suspendTenant: (id, suspend) => api.post(`/admin/tenants/${id}/suspend`, { suspend }).then(res => res.data),
  getGlobalAnalytics: () => api.get('/admin/analytics').then(res => res.data),
  updateSubscription: (id, data) => api.put(`/admin/tenants/${id}/subscription`, data).then(res => res.data),
};

// Default export
export default api;